import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaTrash,
  FaUndo,
  FaHistory,
  FaArrowLeft,
  FaSearch,
  FaClock
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import firebaseService from '../../services/firebaseService';
import './AdminOrders.css';

function DeletedOrders() {
  const navigate = useNavigate();
  const [deletedOrders, setDeletedOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDeletedOrders, setShowDeletedOrders] = useState(true);

  // Auth Protection
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Real-time listener for Deleted Orders
  useEffect(() => {
    console.log('👂 Setting up real-time listener for DELETED orders...');

    // We need to add a subscription method specifically for deleted orders if it doesn't exist
    // For now, let's use the standard get functionality if it exists, but actually it's better to subscribe
    // Let's check firebaseService for subscribeToDeletedOrders

    // Instead of waiting, I'll implement a query here for immediate fix
    const unsubscribe = firebaseService.subscribeToDeletedOrders((result) => {
      if (result.success) {
        setDeletedOrders(result.deletedOrders);
        setLoading(false);
      } else {
        console.error('Failed to sync deleted orders:', result.error);
        setLoading(false);
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Filter logic
  useEffect(() => {
    if (!showDeletedOrders) {
      setFilteredOrders([]);
      return;
    }

    if (!searchTerm.trim()) {
      setFilteredOrders(deletedOrders);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = deletedOrders.filter(order =>
        order.id.toLowerCase().includes(term) ||
        order.customer?.fullName?.toLowerCase().includes(term) ||
        order.customer?.phone1?.includes(term)
      );
      setFilteredOrders(filtered);
    }
  }, [searchTerm, deletedOrders, showDeletedOrders]);

  const handleRestore = async (order) => {
    if (!window.confirm('Restore this order to active list?')) return;

    try {
      const result = await firebaseService.restoreOrder(order);
      if (!result.success) alert('Error: ' + result.error);
    } catch (e) {
      console.error(e);
    }
  };

  const handlePermanentDelete = async (orderId) => {
    if (!window.confirm('FIREBASE: Delete this order PERMANENTLY? This cannot be undone.')) return;

    try {
      // Show local loading or feedback
      const result = await firebaseService.permanentDeleteOrder(orderId);
      if (!result.success) alert('Error: ' + result.error);
      else console.log('✅ Permanent delete successful');
    } catch (e) {
      console.error(e);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="admin-orders-container">
        <div className="admin-loading-state">
          <div className="admin-loading-spinner" />
          <p>Syncing with Firebase...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="admin-orders-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="admin-header">
        <div className="header-top">
          <Link to="/admin/orders" className="back-to-home-btn">
            <FaArrowLeft /> Back to Orders
          </Link>
          <div className="admin-header-content">
            <h1>🗑️ Bin / Deleted Orders</h1>
            <p>Orders here are hidden from the main list</p>
          </div>
        </div>

        <div className="admin-stats">
          <div className="stat-card">
            <FaTrash style={{ color: '#e74c3c' }} />
            <div>
              <span className="stat-number">{deletedOrders.length}</span>
              <span className="stat-label">In Bin</span>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-filters">
        <div className="search-box">
          <FaSearch />
          <input
            placeholder="Search in bin..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          className={`toggle-deleted-orders-btn ${showDeletedOrders ? 'showing' : 'hiding'}`}
          onClick={() => setShowDeletedOrders(!showDeletedOrders)}
        >
          {showDeletedOrders ? '👁️ Hide Bin' : '👁️ Show Bin'}
        </button>
      </div>

      <div className="orders-list">
        <AnimatePresence>
          {filteredOrders.length === 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="no-orders">
              <FaHistory />
              <h3>The bin is empty</h3>
              <p>No deleted orders found matching your search.</p>
            </motion.div>
          ) : (
            filteredOrders.map(order => (
              <motion.div
                key={order.id}
                layout
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="order-card deleted-order"
              >
                <div className="order-header">
                  <div className="order-title-row">
                    <h3>Order #{order.id}</h3>
                    <span className="deleted-badge" style={{ background: '#e74c3c', color: '#fff' }}>DELETED</span>
                  </div>
                  <div className="order-date" style={{ color: '#888' }}>
                    <FaClock /> Deleted on: {formatDate(order.deletedAt)}
                  </div>
                </div>

                <div className="order-customer">
                  <div className="customer-info">👤 {order.customer?.fullName} {order.customer?.lastName}</div>
                  <div className="customer-phone">📞 {order.customer?.phone1}</div>
                  <div className="customer-location">📍 {order.customer?.governorate} - {order.customer?.address}</div>
                </div>

                <div className="order-summary">
                  <div className="order-items">{order.items?.length || 0} Products</div>
                  <div className="order-total" style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>{order.total} EGP</div>
                </div>

                <div className="order-actions">
                  <button onClick={() => handleRestore(order)} className="restore-btn" style={{ background: '#27ae60', color: '#fff' }}>
                    <FaUndo /> Restore
                  </button>
                  <button onClick={() => handlePermanentDelete(order.id)} className="delete-permanently-btn" style={{ background: 'rgba(231, 76, 60, 0.1)', color: '#e74c3c', border: '1px solid #e74c3c' }}>
                    <FaTrash /> Delete Permanently
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default DeletedOrders;
